/**
 * Wallet Isolation for Farcaster-only support
 * Prevents conflicts with other wallet providers by ensuring only Farcaster wallet is used
 */

// Store the original ethereum provider if it exists
let originalEthereum: any = null;

/**
 * Isolates the Farcaster wallet provider by temporarily removing other providers
 * and ensuring only the Farcaster provider is available
 */
export function isolateFarcasterWallet() {
  if (typeof window === 'undefined') return;

  const globalThis = window as any;
  
  // Store the original ethereum provider
  if (globalThis.ethereum && !globalThis.ethereum.__farcasterIsolated) {
    originalEthereum = globalThis.ethereum;
  }

  // If we're in a Farcaster environment, ensure the provider is properly tagged
  if (globalThis.ethereum && globalThis.ethereum.__embedded) {
    // Tag the provider as Farcaster-isolated
    globalThis.ethereum.__farcasterIsolated = true;
    console.log('[wallet-isolation] Farcaster wallet provider isolated');
  }
}

/**
 * Restores the original ethereum provider (if needed)
 */
export function restoreOriginalWallet() {
  if (typeof window === 'undefined') return;
  
  const globalThis = window as any;
  
  if (originalEthereum && !globalThis.ethereum?.__farcasterIsolated) {
    globalThis.ethereum = originalEthereum;
    console.log('[wallet-isolation] Original wallet provider restored');
  }
}

/**
 * Ensures the ethereum provider is properly configured for Farcaster
 */
export function ensureFarcasterProvider() {
  if (typeof window === 'undefined') return false;

  const globalThis = window as any;
  
  // Check if we have a valid ethereum provider
  if (!globalThis.ethereum || typeof globalThis.ethereum.request !== 'function') {
    console.warn('[wallet-isolation] No valid ethereum provider found');
    return false;
  }

  // Ensure the provider is properly tagged
  if (!globalThis.ethereum.__farcasterIsolated) {
    globalThis.ethereum.__farcasterIsolated = true;
  }

  console.log('[wallet-isolation] Farcaster provider verified');
  return true;
}

/**
 * Prevents other wallet providers from overriding the Farcaster provider
 */
export function preventWalletConflicts() {
  if (typeof window === 'undefined') return;

  const globalThis = window as any;
  
  // Override Object.defineProperty to prevent ethereum property redefinition
  const originalDefineProperty = Object.defineProperty;
  
  Object.defineProperty = function(obj: any, prop: string, descriptor: any) {
    // If someone tries to redefine the ethereum property on the global object
    if (prop === 'ethereum' && obj === globalThis) {
      // Check if we already have an ethereum provider (Farcaster or other)
      if (globalThis.ethereum) {
        console.warn('[wallet-isolation] Blocked attempt to redefine ethereum property - provider already exists');
        return obj; // Return the object without redefining the property
      }
    }
    
    return originalDefineProperty.call(this, obj, prop, descriptor);
  };
  
  // Also override Object.defineProperties for bulk property definitions
  const originalDefineProperties = Object.defineProperties;
  
  Object.defineProperties = function(obj: any, props: any) {
    // If someone tries to define ethereum property in bulk
    if (obj === globalThis && props.ethereum && globalThis.ethereum) {
      console.warn('[wallet-isolation] Blocked attempt to redefine ethereum property via defineProperties');
      // Remove ethereum from the properties to define
      const { ethereum, ...otherProps } = props;
      return originalDefineProperties.call(this, obj, otherProps);
    }
    
    return originalDefineProperties.call(this, obj, props);
  };
  
  console.log('[wallet-isolation] Wallet conflict prevention enabled');
}
